import { Route, Routes } from 'react-router';
import { AdminLayout } from './layouts/AdminLayout';
import { Protected } from './layouts/Protected';
import { SessionOnly } from './layouts/SessionOnly';
import { Account } from './pages/Account';
import { AdminBotDetail } from './pages/AdminBotDetail';
import { AdminBots } from './pages/AdminBots';
import { AdminFeedback } from './pages/AdminFeedback';
import { AdminJobDetail } from './pages/AdminJobDetail';
import { AdminJobs } from './pages/AdminJobs';
import { AdminPlayground } from './pages/AdminPlayground';
import { AdminPostEditor } from './pages/AdminPostEditor';
import { AdminPosts } from './pages/AdminPosts';
import { AdminWorkspaceDetail } from './pages/AdminWorkspaceDetail';
import { AdminWorkspaces } from './pages/AdminWorkspaces';
import { BotAppearance } from './pages/BotAppearance';
import { BotConversations } from './pages/BotConversations';
import { BotLeads } from './pages/BotLeads';
import { BotOverview } from './pages/BotOverview';
import { BotSettings } from './pages/BotSettings';
import { BotSources } from './pages/BotSources';
import { BotUnanswered } from './pages/BotUnanswered';
import { Billing } from './pages/Billing';
import { Bots } from './pages/Bots';
import { CheckEmail } from './pages/CheckEmail';
import { InviteAccept } from './pages/InviteAccept';
import { LandingResolver } from './pages/LandingResolver';
import { Members } from './pages/Members';
import { NotFound } from './pages/NotFound';
import { Onboarding } from './pages/Onboarding';
import { Playground } from './pages/Playground';
import { RequestReset } from './pages/RequestReset';
import { ResetPassword } from './pages/ResetPassword';
import { SignIn } from './pages/SignIn';
import { SignUp } from './pages/SignUp';
import { SourceDetail } from './pages/SourceDetail';
import { Unsubscribe } from './pages/Unsubscribe';
import { VerifyEmail } from './pages/VerifyEmail';
import { WorkspaceSettings } from './pages/WorkspaceSettings';

export function App() {
  return (
    <Routes>
      <Route path="/sign-in" element={<SignIn />} />
      <Route path="/sign-up" element={<SignUp />} />
      <Route path="/check-email" element={<CheckEmail />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      {/* M6 Task 5 / spec D2 — the last auth surface missing. `RequestReset` posts via
          better-auth's `requestPasswordReset`; `ResetPassword` reads the `?token=` query
          param better-auth's own redirect handler appends (see that file's doc comment). */}
      <Route path="/forgot-password" element={<RequestReset />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      {/* workspace-members cycle (THI-110 Task 5) — session-aware like SignIn/SignUp above,
          NOT wrapped in `Protected`/`SessionOnly`: an unauthenticated visitor arriving from an
          invite email is exactly who this page is for, and `Protected` would bounce them to
          `/sign-in` before they ever saw which workspace invited them. `InviteAccept.tsx` reads
          `useSession()` itself and branches on it directly. */}
      <Route path="/invite/:token" element={<InviteAccept />} />
      {/* bot-notifications design (2026-08-21) Task 5 — every notification email's one-click
          unsubscribe link posts here with no auth (`POST /api/notifications/unsubscribe`, Task 4
          is fully public). Same reasoning as `/invite/:token` just above: the visitor arriving
          from an email link is exactly who this route is for, session or not. */}
      <Route path="/unsubscribe" element={<Unsubscribe />} />
      {/* Fix-wave item 2 (spec §8: "`/onboarding`. Stays outside the shell, full-width,
          unchanged. A zero-bot workspace has no bot to scope a rail to..."). `SessionOnly`
          runs the same signed-in guard `Protected` does, without the bot-scoped rail — see
          that layout's own comment for why this couldn't stay nested under `Protected`. */}
      <Route element={<SessionOnly />}>
        {/* D6/M5 task-5: the golden-path wizard, auto-entered from "/" on a zero-bot
            workspace (now via the landing resolver rather than a `?stay=1` escape hatch —
            `/bots` is its own path, so the empty state there is reachable on purpose). */}
        <Route path="/onboarding" element={<Onboarding />} />
      </Route>
      <Route element={<Protected />}>
        {/* Task 7 (dashboard-shell cycle, handoff §8): "/" no longer renders the bots list —
            it resolves to the last-used bot's Overview, falling back to the first bot or to
            `/onboarding` on a zero-bot workspace. The list itself lives at `/bots` below. */}
        <Route path="/" element={<LandingResolver />} />
        <Route path="/bots" element={<Bots />} />
        {/* Hygiene-cycle spec §C: workspace-member-level, linked from the header UserMenu
            above Billing — profile name + password, not nested under /bots. */}
        <Route path="/account" element={<Account />} />
        {/* D7/D8: workspace-level, linked from the app header — not nested under /bots. */}
        <Route path="/billing" element={<Billing />} />
        {/* workspace-members cycle (THI-110 Task 5) — workspace-level, beside Billing; now
            linked from the rail's workspace menu for owner|admin, but the route itself stays reachable
            so a deep link from anyone else renders `Members.tsx`'s own "ask your owner or
            admin" card off the API's 403, rather than a route-level 404 that would look like
            the link itself was broken. */}
        <Route path="/members" element={<Members />} />
        {/* workspace-first shell (2026-08-21) — reachable for every role; the page renders
            its own owner/admin card for members, same idiom as /members and /billing. */}
        <Route path="/workspace" element={<WorkspaceSettings />} />
        {/* Task 3 of the admin-platform cycle (spec §4) — internal ops screens, guarded by
            `AdminLayout` on `session.user.role === 'admin'`. Admin v2 (spec
            docs/superpowers/specs/2026-08-06-admin-v2-internal-workspaces-design.md §B) adds
            the workspace/bot/job detail pages nested under the same layout. */}
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminWorkspaces />} />
          <Route path="workspaces/:id" element={<AdminWorkspaceDetail />} />
          <Route path="bots" element={<AdminBots />} />
          <Route path="bots/:id" element={<AdminBotDetail />} />
          <Route path="jobs" element={<AdminJobs />} />
          <Route path="jobs/:id" element={<AdminJobDetail />} />
          {/* Blog/SEO cycle Task 3 (spec §5) — `/admin/posts/new` and `/admin/posts/:id`
              share one editor component; `new` never matches the `:id` route below it
              because react-router resolves static segments before dynamic ones. */}
          <Route path="posts" element={<AdminPosts />} />
          <Route path="posts/new" element={<AdminPostEditor />} />
          <Route path="posts/:id" element={<AdminPostEditor />} />
          {/* Task 4 of the feedback-form cycle (spec docs/superpowers/specs/
              2026-08-18-feedback-form-design.md "Dashboard — admin side") — the triage list;
              no detail sub-route (spec: "single handled boolean, no pipeline, no threads"). */}
          <Route path="feedback" element={<AdminFeedback />} />
          {/* Task 2 of the playground-analytics cycle (spec .superpowers/sdd/
              2026-08-20-playground-analytics/) — demo-widget usage overview, fixed-shape, no
              detail sub-route (same non-paginated shape as the DTO it consumes). */}
          <Route path="playground" element={<AdminPlayground />} />
        </Route>
        {/* D2: /bots/:botId is now the Overview tab; Sources moved to its own subpath. */}
        <Route path="/bots/:botId" element={<BotOverview />} />
        <Route path="/bots/:botId/sources" element={<BotSources />} />
        {/* Task 5 (source-detail-diagnostics) — what got indexed, and what did not, and why.
            A page, not a dialog: see SourceDetail.tsx's own header comment. */}
        <Route path="/bots/:botId/sources/:sid" element={<SourceDetail />} />
        <Route path="/bots/:botId/playground" element={<Playground />} />
        <Route path="/bots/:botId/conversations" element={<BotConversations />} />
        <Route path="/bots/:botId/leads" element={<BotLeads />} />
        <Route path="/bots/:botId/unanswered" element={<BotUnanswered />} />
        <Route path="/bots/:botId/appearance" element={<BotAppearance />} />
        <Route path="/bots/:botId/settings" element={<BotSettings />} />
      </Route>
      {/* Outside `Protected` on purpose: a wrong address should say so whether or not anyone
          is signed in, rather than bouncing to sign-in and losing the fact that the URL was
          bad. Last, so it only catches what nothing above matched. */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
